const AudioContextCtor = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;

const computeRms = (analyser, floatBuffer, byteBufferRef) => {
  if (!analyser) return { rms: 0, byteBuffer: byteBufferRef };
  if (analyser.getFloatTimeDomainData) {
    analyser.getFloatTimeDomainData(floatBuffer);
    let sum = 0;
    for (let i = 0; i < floatBuffer.length; i += 1) {
      const sample = floatBuffer[i];
      sum += sample * sample;
    }
    return { rms: Math.sqrt(sum / floatBuffer.length), byteBuffer: byteBufferRef };
  }

  let byteBuffer = byteBufferRef;
  if (!byteBuffer || byteBuffer.length !== floatBuffer.length) {
    byteBuffer = new Uint8Array(floatBuffer.length);
  }
  analyser.getByteTimeDomainData(byteBuffer);
  let sum = 0;
  for (let i = 0; i < byteBuffer.length; i += 1) {
    const normalized = (byteBuffer[i] - 128) / 128;
    sum += normalized * normalized;
  }
  return { rms: Math.sqrt(sum / byteBuffer.length), byteBuffer };
};

export const createAudioMeter = ({
  stream,
  audioContext = null,
  threshold = 0.01,
  windowMs = 1400,
  sampleMs = 120,
  fftSize = 2048,
  smoothing = 0.7,
  onUpdate,
} = {}) => {
  let context = audioContext;
  let ownsContext = false;
  let analyser = null;
  let source = null;
  let floatBuffer = null;
  let byteBuffer = null;
  let interval = null;
  let lastSignalAt = 0;
  let currentRms = 0;
  let hasSignal = false;

  const start = async () => {
    if (!stream || !AudioContextCtor) return false;
    if (!context) {
      context = new AudioContextCtor();
      ownsContext = true;
    }
    try {
      await context.resume();
    } catch (error) {
      console.warn('AudioContext resume failed', error);
    }

    analyser = context.createAnalyser();
    analyser.fftSize = fftSize;
    analyser.smoothingTimeConstant = smoothing;
    source = context.createMediaStreamSource(stream);
    source.connect(analyser);
    floatBuffer = new Float32Array(analyser.fftSize);

    interval = setInterval(() => {
      const result = computeRms(analyser, floatBuffer, byteBuffer);
      byteBuffer = result.byteBuffer;
      currentRms = result.rms;
      if (currentRms >= threshold) {
        lastSignalAt = Date.now();
      }
      const now = Date.now();
      hasSignal = now - lastSignalAt <= windowMs;
      if (typeof onUpdate === 'function') {
        onUpdate({ rms: currentRms, hasSignal });
      }
    }, sampleMs);

    return true;
  };

  const stop = async () => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
    if (source) {
      try {
        source.disconnect();
      } catch (error) {
        console.warn('Audio meter source disconnect failed', error);
      }
    }
    if (analyser) {
      try {
        analyser.disconnect();
      } catch (error) {
        console.warn('Audio meter analyser disconnect failed', error);
      }
    }
    analyser = null;
    source = null;
    floatBuffer = null;
    byteBuffer = null;
    currentRms = 0;
    hasSignal = false;
    if (ownsContext && context) {
      try {
        await context.close();
      } catch (error) {
        console.warn('AudioContext close failed', error);
      }
      context = null;
      ownsContext = false;
    }
  };

  const snapshot = () => ({ rms: currentRms, hasSignal });

  return { start, stop, snapshot };
};
